import React from "react";
import {
  Button,
  Drawer,
  IconButton,
  List,
  Divider,
  useMediaQuery,
  useTheme,
  Box,
  Collapse,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import { navigationConfig } from "../../config/navigation";
import {
  ChevronDown,
  ChevronRight,
  LogOut,
  User,
  PanelLeft,
} from "lucide-react";
import { useLocation, Link, useNavigate } from "react-router";
import { useDrawer } from "../../services/providers/DrawerContext";
import { useAuthStore } from "../../store/authstore";
import { NavItem } from "../../types/common";
import { trackEvent, trackNavigation } from "../../lib/mixpanel";
import { useUnreadCount } from "../../hooks/useNotifications";
import { usePendingApprovalsCount } from "../../hooks/useExpenseApprovals";
import { ROUTES } from "../../router/routes";
import { useInstituteFeatureFlag } from "../../hooks/useInstituteFeatureFlag";

interface SidebarProps {
  mobileOpen: boolean;
  handleDrawerToggle: () => void;
}

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "depth" && prop !== "collapsed",
})<{
  active?: boolean;
  depth?: number;
  collapsed?: boolean;
}>(({ theme, active, depth = 0, collapsed }) => ({
  borderRadius: 10,
  marginBottom: 4,
  marginLeft: collapsed ? 4 : depth * 12 + 4,
  marginRight: collapsed ? 4 : 4,
  paddingLeft: collapsed ? 0 : 12 + depth * 12,
  paddingRight: collapsed ? 0 : 12,
  justifyContent: collapsed ? "center" : "flex-start",
  minHeight: collapsed ? 42 : 40,
  width: collapsed ? "auto" : undefined,
  color: active ? theme.palette.secondary.main : "#ffffff",
  backgroundColor: active ? "rgba(255, 255, 255, 0.08)" : "transparent",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.12)",
  },
  transition: "all 0.2s ease",
}));

/** Total column width reserved for the floating sidebar (includes outer gap). */
export const SIDEBAR_EXPANDED_WIDTH = 300;
export const SIDEBAR_COLLAPSED_WIDTH = 72;
export const SIDEBAR_OUTER_GAP = 8;

const Sidebar: React.FC<SidebarProps> = ({
  mobileOpen,
  handleDrawerToggle,
}) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { open, toggleDrawer } = useDrawer();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [openSubMenus, setOpenSubMenus] = React.useState<
    Record<string, boolean>
  >({});
  const [flyout, setFlyout] = React.useState<{
    anchorEl: HTMLElement;
    title: string;
    children: NavItem[];
    icon?: NavItem["icon"] | typeof PanelLeft;
    path?: string;
    onSelect?: () => void;
  } | null>(null);
  const flyoutCloseTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearFlyoutCloseTimer = () => {
    if (flyoutCloseTimer.current) {
      clearTimeout(flyoutCloseTimer.current);
      flyoutCloseTimer.current = null;
    }
  };

  const openFlyout = (
    anchorEl: HTMLElement,
    title: string,
    children: NavItem[],
    icon?: NavItem["icon"] | typeof PanelLeft,
    path?: string,
    onSelect?: () => void
  ) => {
    clearFlyoutCloseTimer();
    setFlyout({ anchorEl, title, children, icon, path, onSelect });
  };

  const scheduleFlyoutClose = () => {
    clearFlyoutCloseTimer();
    flyoutCloseTimer.current = setTimeout(() => setFlyout(null), 180);
  };

  const handleSubMenuToggle = (path: string) => {
    setOpenSubMenus((prev) => ({
      ...prev,
      [path]: !prev[path],
    }));
  };

  const isDesktopExpanded = isMobile ? true : open;
  const drawerWidth = isMobile
    ? 0
    : open
      ? SIDEBAR_EXPANDED_WIDTH
      : SIDEBAR_COLLAPSED_WIDTH;
  const cardWidth = drawerWidth - SIDEBAR_OUTER_GAP * 2;
  // Automatically open menu items for active route
  React.useEffect(() => {
    if (location.pathname !== "/") {
      const pathSegments = location.pathname.split("/").filter(Boolean);
      let currentPath = "";

      // Build paths progressively and open corresponding menus
      const newOpenMenus: Record<string, boolean> = {};
      pathSegments.forEach((segment, index) => {
        if (index === 0) {
          currentPath = `/${segment}`;
        } else {
          currentPath = `${currentPath}/${segment}`;
        }

        // Don't open the last segment (current page)
        if (index < pathSegments.length - 1) {
          newOpenMenus[currentPath] = true;
        }
      });

      setOpenSubMenus((prev) => ({ ...prev, ...newOpenMenus }));
    } else {
      // If on home page, close all submenus
      setOpenSubMenus({});
    }
  }, [location.pathname]);

  const activeRoute = (routeName: string) => {
    return location.pathname === routeName;
  };

  const activeParentRoute = (routeName: string) => {
    return location.pathname.startsWith(routeName) && routeName !== "/";
  };

  const { hasPermission, hasRoleType, hasRole, session, logout } = useAuthStore();
  const isFeesEnabled = useInstituteFeatureFlag('fees');
  const flagMap: Record<string, boolean> = { fees: isFeesEnabled };
  const { data: unreadData } = useUnreadCount();
  const unreadMessages = unreadData?.unreadTotalMessages ?? 0;
  const { data: pendingApprovalsCount = 0, isLoading: isPendingCountLoading } = usePendingApprovalsCount();

  const handleLogout = () => {
    trackEvent('Logout Clicked', { source: 'sidebar' });
    logout();
    navigate(ROUTES.AUTH.LOGIN);
  };

  const handleProfileClick = () => {
    trackEvent('Profile Clicked', { source: 'sidebar' });
    navigate(ROUTES.USER.PROFILE);
    if (isMobile) handleDrawerToggle();
  };

  // Inject live badge counts into nav items
  const navConfigWithBadges = React.useMemo(() => navigationConfig.map((item) => {
    if (!item.children) return item;
    return {
      ...item,
      children: item.children.map((child) => {
        if (child.path === ROUTES.COMMUNICATION.MESSAGES)
          return { ...child, badge: unreadMessages > 0 ? unreadMessages : undefined };
        if (child.path === ROUTES.EXPENSES.APPROVALS.PENDING)
          return { ...child, badge: pendingApprovalsCount > 0 ? pendingApprovalsCount : undefined };
        return child;
      }),
    };
  }), [unreadMessages, pendingApprovalsCount]);

  // Check if user has access to a navigation item
  const hasAccess = (item: NavItem) => {
    if (item.hideForEmployeeWhenNoPendingApprovals && hasRoleType('employee') && !isPendingCountLoading && pendingApprovalsCount === 0)
      return false;
    return (
      (!item.requiredPermission || hasPermission(item.requiredPermission)) &&
      (!item.requiredRoleType || hasRoleType(item.requiredRoleType)) &&
      (!item.roles || item.roles.some((role: string) => hasRole(role))) &&
      (!item.featureFlag || flagMap[item.featureFlag] !== false)
    );
  };

  const drawer = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        color: "#fff",
        backgroundColor: theme.palette.customBackground?.sidebar,
        borderRadius: "25px",
        overflow: "hidden",
      }}
    >
      <List
        sx={{
          pt: 1.25,
          pb: 1.25,
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          backgroundColor: theme.palette.customBackground?.sidebar,
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {/* Collapsed: toggle as first icon row */}
        {!isMobile && !isDesktopExpanded && (
          <ListItem disablePadding sx={{ display: "block" }}>
            <Box
              onMouseEnter={(e) =>
                openFlyout(
                  e.currentTarget,
                  open ? "Close sidebar" : "Open sidebar",
                  [],
                  PanelLeft,
                  undefined,
                  () => {
                    toggleDrawer();
                    trackEvent("Sidebar Toggled", {
                      platform: "desktop",
                      action: open ? "close" : "open",
                      source: "sidebar-row",
                    });
                    setFlyout(null);
                  }
                )
              }
              onMouseLeave={scheduleFlyoutClose}
            >
              <StyledListItemButton
                collapsed
                onClick={() => {
                  toggleDrawer();
                  trackEvent("Sidebar Toggled", {
                    platform: "desktop",
                    action: open ? "close" : "open",
                    source: "sidebar-row",
                  });
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: 0,
                    justifyContent: "center",
                    color: "#ffffff",
                  }}
                >
                  <PanelLeft size={22} />
                </ListItemIcon>
              </StyledListItemButton>
            </Box>
          </ListItem>
        )}
        {navConfigWithBadges.map((item) => {
          if (!hasAccess(item)) {
            return null;
          }

          const renderMenuItem = (item: NavItem, level: number = 0) => {
            const isActiveParent = activeParentRoute(item.path ?? "");
            const isActive = activeRoute(item.path ?? "");

            const accessibleChildren = item.children
              ? item.children.filter((subItem: NavItem) => hasAccess(subItem))
              : [];

            if (item.children && accessibleChildren.length === 0) {
              return null;
            }

            // Collapsed rail: show top-level icons only
            if (!isDesktopExpanded && level > 0) {
              return null;
            }

            const itemPath = item.path ?? "";
            const hasChildren = accessibleChildren.length > 0;
            const isOpenSubMenu = openSubMenus[itemPath] === true;
            const isDashboardRow =
              level === 0 &&
              (item.title === "Dashboard" ||
                itemPath === ROUTES.DASHBOARD.ROOT ||
                itemPath === ROUTES.EMPLOYEE.DASHBOARD);

            const handleToggleClick = (e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              toggleDrawer();
              trackEvent("Sidebar Toggled", {
                platform: "desktop",
                action: open ? "close" : "open",
                source: "dashboard-row",
              });
            };

            const button = (
              <StyledListItemButton
                component={hasChildren ? "div" : Link}
                to={!hasChildren ? item.path : undefined}
                active={isActive || (!isDesktopExpanded && isActiveParent)}
                depth={level}
                collapsed={!isDesktopExpanded}
                onClick={() => {
                  if (hasChildren) {
                    if (isDesktopExpanded) {
                      handleSubMenuToggle(itemPath);
                      trackNavigation(item.title, location.pathname, "Submenu");
                    }
                  } else {
                    trackNavigation(item.path ?? "", location.pathname, item.title);
                    if (isMobile) handleDrawerToggle();
                  }
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: isDesktopExpanded ? 2 : 0,
                    justifyContent: "center",
                    color:
                      isActiveParent || isActive
                        ? "secondary.main"
                        : "#ffffff",
                  }}
                >
                  {item.icon && <item.icon size={isDesktopExpanded ? 20 : 22} />}
                </ListItemIcon>
                {isDesktopExpanded && (
                  <>
                    <ListItemText
                      primary={item.title}
                      primaryTypographyProps={{
                        fontSize: level > 0 ? "0.875rem" : "1rem",
                      }}
                      sx={{
                        color:
                          isActiveParent || isActive
                            ? "secondary.main"
                            : "inherit",
                        "& .MuiTypography-root": {
                          fontWeight: 500,
                        },
                      }}
                    />
                    {item.badge && item.badge > 0 && (
                      <Box
                        sx={{
                          minWidth: 20,
                          height: 20,
                          borderRadius: "10px",
                          bgcolor: "#EF4444",
                          color: "#fff",
                          fontSize: "0.6rem",
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          px: "5px",
                          ml: 0.5,
                          flexShrink: 0,
                        }}
                      >
                        {item.badge > 99 ? "99+" : item.badge}
                      </Box>
                    )}
                    {hasChildren &&
                      (isOpenSubMenu ? (
                        <ChevronDown size={16} />
                      ) : (
                        <ChevronRight size={16} />
                      ))}
                    {isDashboardRow && !isMobile && (
                      <Box
                        component="span"
                        role="button"
                        aria-label={open ? "Close sidebar" : "Open sidebar"}
                        title={open ? "Close sidebar" : "Open sidebar"}
                        onClick={handleToggleClick}
                        sx={{
                          ml: "auto",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          p: 0.75,
                          borderRadius: 1.5,
                          color: "#ffffff",
                          flexShrink: 0,
                          "&:hover": {
                            bgcolor: "rgba(255,255,255,0.12)",
                            color: "#ffffff",
                          },
                        }}
                      >
                        <PanelLeft size={18} color="#ffffff" />
                      </Box>
                    )}
                  </>
                )}
              </StyledListItemButton>
            );

            return (
              <React.Fragment key={`${itemPath}-${item.title}`}>
                <ListItem
                  disablePadding
                  sx={{
                    display: "block",
                    backgroundColor: theme.palette.customBackground?.sidebar,
                  }}
                >
                  {!isDesktopExpanded ? (
                    <Box
                      onMouseEnter={(e) =>
                        openFlyout(
                          e.currentTarget,
                          item.title,
                          accessibleChildren,
                          item.icon,
                          item.path
                        )
                      }
                      onMouseLeave={scheduleFlyoutClose}
                    >
                      {button}
                    </Box>
                  ) : (
                    button
                  )}
                </ListItem>
                {hasChildren && isDesktopExpanded && (
                  <Collapse
                    in={isOpenSubMenu}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List
                      component="div"
                      disablePadding
                      sx={{
                        backgroundColor:
                          theme.palette.customBackground?.sidebar,
                      }}
                    >
                      {accessibleChildren.map((subItem: NavItem) =>
                        renderMenuItem(subItem, level + 1)
                      )}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            );
          };

          return renderMenuItem(item);
        })}
      </List>

      <Popover
        open={Boolean(flyout) && !isDesktopExpanded}
        anchorEl={flyout?.anchorEl ?? null}
        onClose={() => setFlyout(null)}
        // Title row lines up with the rail icon; children stack below
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableRestoreFocus
        sx={{ pointerEvents: "none" }}
        slotProps={{
          paper: {
            onMouseEnter: clearFlyoutCloseTimer,
            onMouseLeave: scheduleFlyoutClose,
            sx: {
              pointerEvents: "auto",
              ml: 0,
              mt: 0,
              minWidth: flyout && flyout.children.length > 0 ? 200 : "auto",
              borderRadius: "0 12px 12px 0",
              bgcolor: theme.palette.customBackground?.sidebar ?? "#1a1b1e",
              color: "#fff",
              border: "none",
              boxShadow: "4px 8px 24px rgba(0,0,0,0.35)",
              overflow: "hidden",
            },
          },
        }}
      >
        {flyout && (
          <Box>
            {(() => {
              // Icon stays in the sidebar rail — flyout only shows label (+ children)
              const isLeafLink =
                flyout.children.length === 0 && Boolean(flyout.path);
              const isActionOnly =
                flyout.children.length === 0 && Boolean(flyout.onSelect);

              const chipContent = (
                <>
                  <Box
                    sx={{
                      width: 2,
                      height: 22,
                      mr: 1.5,
                      borderRadius: 1,
                      background:
                        "linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.55) 50%, transparent 100%)",
                      boxShadow: "0 0 10px 1px rgba(255,255,255,0.35)",
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: "rgba(255,255,255,0.72)",
                      whiteSpace: "nowrap",
                      lineHeight: 1.2,
                      pr: 0.5,
                    }}
                  >
                    {flyout.title}
                  </Typography>
                </>
              );

              const chipSx = {
                display: "flex",
                alignItems: "center",
                gap: 0,
                pr: 2,
                pl: 1,
                // Match collapsed rail icon row height so title sits beside the icon
                height: 42,
                boxSizing: "border-box",
                textDecoration: "none",
                color: "inherit",
                cursor: isLeafLink || isActionOnly ? "pointer" : "default",
              } as const;

              if (isLeafLink && flyout.path) {
                return (
                  <Box
                    component={Link}
                    to={flyout.path}
                    onClick={() => {
                      trackNavigation(
                        flyout.path!,
                        location.pathname,
                        flyout.title
                      );
                      setFlyout(null);
                    }}
                    sx={chipSx}
                  >
                    {chipContent}
                  </Box>
                );
              }

              if (isActionOnly && flyout.onSelect) {
                return (
                  <Box
                    role="button"
                    onClick={() => flyout.onSelect?.()}
                    sx={chipSx}
                  >
                    {chipContent}
                  </Box>
                );
              }

              return <Box sx={chipSx}>{chipContent}</Box>;
            })()}

            {flyout.children.length > 0 && (
              <List
                disablePadding
                dense
                sx={{
                  // Same left edge as STUDENTS header row; text lines up with title
                  pl: 1,
                  pr: 1,
                  pb: 0.75,
                  pt: 0,
                }}
              >
                {flyout.children.map((child) => {
                  const nested =
                    child.children?.filter((c) => hasAccess(c)) ?? [];
                  const entries =
                    nested.length > 0
                      ? nested.map((n) => ({
                          key: `${child.title}-${n.path}`,
                          title: n.title,
                          path: n.path ?? "#",
                          group: child.title,
                        }))
                      : child.path
                        ? [
                            {
                              key: `${child.title}-${child.path}`,
                              title: child.title,
                              path: child.path,
                              group: null as string | null,
                            },
                          ]
                        : [];

                  return entries.map((entry) => {
                    const selected = activeRoute(entry.path);
                    return (
                      <ListItem key={entry.key} disablePadding>
                        <ListItemButton
                          component={Link}
                          to={entry.path}
                          selected={selected}
                          onClick={() => {
                            trackNavigation(
                              entry.path,
                              location.pathname,
                              entry.title
                            );
                            setFlyout(null);
                          }}
                          sx={{
                            mx: 0,
                            mb: 0.25,
                            // 2px glow + 12px gap = align label with STUDENTS text
                            pl: "14px",
                            pr: 1.5,
                            py: 0.75,
                            borderRadius: 1.5,
                            color: selected ? "secondary.main" : "#fff",
                            "&.Mui-selected": {
                              bgcolor: "rgba(255,255,255,0.1)",
                              color: "secondary.main",
                            },
                            "&:hover": {
                              bgcolor: "rgba(255,255,255,0.12)",
                            },
                          }}
                        >
                          <ListItemText
                            primary={entry.title}
                            primaryTypographyProps={{
                              fontSize: "0.875rem",
                              fontWeight: 600,
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  });
                })}
              </List>
            )}
          </Box>
        )}
      </Popover>

      {/* Fixed profile + logout at bottom */}
      <Divider sx={{ backgroundColor: "rgba(255,255,255,0.1)", flexShrink: 0 }} />
      <Box
        sx={{
          flexShrink: 0,
          px: isDesktopExpanded ? 1.5 : 0.5,
          pt: 1.5,
          pb: 1.75,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          alignItems: isDesktopExpanded ? "stretch" : "center",
        }}
      >
        {isDesktopExpanded ? (
          <>
            <Button
              fullWidth
              onClick={handleProfileClick}
              startIcon={<User size={16} />}
              sx={{
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                py: 1,
                color: "#fff",
                bgcolor: "rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.12)",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.16)",
                },
              }}
            >
              Profile
            </Button>
            <Button
              fullWidth
              onClick={handleLogout}
              startIcon={<LogOut size={16} />}
              sx={{
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                py: 1,
                color: "#fff",
                bgcolor: "rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.12)",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.16)",
                },
              }}
            >
              Logout
            </Button>
          </>
        ) : (
          <>
            <Tooltip title="Profile" placement="right">
              <IconButton
                onClick={handleProfileClick}
                aria-label="Profile"
                sx={{
                  color: "#fff",
                  width: 40,
                  height: 40,
                  bgcolor: "rgba(255,255,255,0.1)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.16)" },
                }}
              >
                <User size={20} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Logout" placement="right">
              <IconButton
                onClick={handleLogout}
                aria-label="Logout"
                sx={{
                  color: "#fff",
                  width: 40,
                  height: 40,
                  bgcolor: "rgba(255,255,255,0.1)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.16)" },
                }}
              >
                <LogOut size={20} />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      <Box
        sx={{
          width: { md: drawerWidth },
          flexShrink: { md: 0 },
          zIndex: (theme) => theme.zIndex.drawer,
          padding: { md: `${SIDEBAR_OUTER_GAP}px` },
          height: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "visible",
          position: "relative",
          transition: (t) =>
            t.transitions.create("width", {
              easing: t.transitions.easing.sharp,
              duration: t.transitions.duration.enteringScreen,
            }),
        }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: 280,
              backgroundColor: theme.palette.customBackground?.sidebar,
              borderRadius: "0 16px 16px 0",
            },
          }}
        >
          {drawer}
        </Drawer>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            position: "relative",
          }}
        >
          <Drawer
            variant="permanent"
            sx={{
              display: "block",
              height: "100%",
              flex: 1,
              minHeight: 0,
              "& .MuiDrawer-paper": {
                width: cardWidth,
                position: "relative",
                borderRadius: "25px",
                height: "100%",
                overflow: "visible",
                backgroundColor: theme.palette.customBackground?.sidebar,
                border: 0,
                boxShadow: theme.shadows[8],
                transition: theme.transitions.create("width", {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
              },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>
      </Box>
    </>
  );
};

export default Sidebar;
